/**
 * LEK (Local Ecological Knowledge) API client.
 * Communicates with the LEK API for species, habitat, and catchability data.
 *
 * Species can be specified by numeric species_id or species_uuid string.
 * Path and query params accept either; the backend resolves to species_id.
 */

import axios from 'axios';
import { useCallback } from 'react';
import { getEnvVar } from '../utils/env';
import { withAuth } from './auth';

export const LEK_API_URL =
  getEnvVar('REACT_APP_LEK_API_BASE_URL') ||
  getEnvVar('REACT_APP_LEK_API') ||
  'https://lek.onrender.com/api';

const lekApi = axios.create({
  baseURL: LEK_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

lekApi.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('LEK API Request Error:', error);
    return Promise.reject(error);
  }
);

lekApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(
      `LEK API Error: ${error.response?.status || 'No Status'} from ${error.config?.url || 'Unknown URL'}`
    );
    console.error('LEK API Error details:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Hook that provides LEK API functions with authentication.
 * Must be called with apiCall from useApi (or equivalent).
 *
 * @param {Function} apiCall - The shared apiCall wrapper that handles auth and error redirects
 * @returns {Object} LEK API functions
 */
export function useLekApi(apiCall) {
  const checkLekConnectionHealth = useCallback(async () => {
    return await apiCall(async (token) => {
      return await lekApi.get('/health', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getLekSpecies = useCallback(async () => {
    return await apiCall(async (token) => {
      return await lekApi.get('/species', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getLekStatistics = useCallback(async () => {
    return await apiCall(async (token) => {
      return await lekApi.get('/statistics', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  /**
   * @param {number|string} speciesIdentifier - Numeric species_id or species_uuid string
   */
  const getLekHabitatData = useCallback(
    async (speciesIdentifier, monthRange = null, season = null) => {
      return await apiCall(async (token) => {
        const params = new URLSearchParams();
        if (speciesIdentifier != null && speciesIdentifier !== '')
          params.append('species', speciesIdentifier);
        if (monthRange?.start)
          params.append('month_start', monthRange.start.toString());
        if (monthRange?.end)
          params.append('month_end', monthRange.end.toString());
        if (season) params.append('season', season.toString());

        const url = `/habitat-data${params.toString() ? `?${params.toString()}` : ''}`;
        return await lekApi.get(url, { headers: withAuth({}, token) });
      });
    },
    [apiCall]
  );

  /**
   * @param {number|string} speciesIdentifier - Numeric species_id or species_uuid string
   */
  const getLekGridCellDetails = useCallback(
    async (gridId, speciesIdentifier, monthStart, monthEnd) => {
      return await apiCall(async (token) => {
        const params = new URLSearchParams({
          species: speciesIdentifier,
          month_start: monthStart,
          month_end: monthEnd,
        });
        const url = `/grid-detail/${gridId}?${params.toString()}`;
        return await lekApi.get(url, { headers: withAuth({}, token) });
      });
    },
    [apiCall]
  );

  /** @param {number|string} species - Numeric species_id or species_uuid string (path) */
  const getLekCatchabilityData = useCallback(
    async (species, startDate, endDate) => {
      return await apiCall(async (token) => {
        const url = `/catchability-full/${species}?start=${startDate}&end=${endDate}`;
        return await lekApi.get(url, { headers: withAuth({}, token) });
      });
    },
    [apiCall]
  );

  /** @param {number|string} species - Numeric species_id or species_uuid string (path) */
  const getSpeciesLEK = useCallback(
    async (species) => {
      return await apiCall(async (token) => {
        const url = `/species-lek/${species}`;
        return await lekApi.get(url, { headers: withAuth({}, token) });
      });
    },
    [apiCall]
  );

  /** @param {number|string} species - Numeric species_id or species_uuid string (path) */
  const getLekParameters = useCallback(
    async (species) => {
      return await apiCall(async (token) => {
        return await lekApi.get(`/catchability-params/${species}`, {
          headers: withAuth({}, token),
        });
      });
    },
    [apiCall]
  );

  return {
    checkLekConnectionHealth,
    getLekSpecies,
    getLekStatistics,
    getLekHabitatData,
    getLekGridCellDetails,
    getLekCatchabilityData,
    getSpeciesLEK,
    getLekParameters,
  };
}
